import type { BotSpec } from '@halvard/ai';
import type { CameraView, Square } from '@halvard/board3d';
import {
  agreeDraw,
  applyAction,
  isPinned,
  newClassicGame,
  newSeededGame,
  otherTeam,
  resign,
  viewFor,
  type GameState,
  type PieceAction,
  type Team,
} from '@halvard/rules';
import { createStore } from 'zustand/vanilla';
import { describeAction, describeOutcome } from './announce';
import { actionAt, availableActions, type MasonMode } from './hints';
import { configFor, type Mountain } from './mountain';
import { TEAM_LABEL } from './pieceInfo';

export type { Mountain } from './mountain';

export type Player = { kind: 'human' } | { kind: 'bot'; spec: BotSpec };
export type Players = Record<Team, Player>;

/** Whose view the screen shows under fog: the side to move, one team, or the whole board. */
export type Perspective = Team | 'turn' | 'all';

export const TWO_HUMANS: Players = { I: { kind: 'human' }, B: { kind: 'human' } };

export interface LocalGameState {
  game: GameState;
  /** Previous states, for undo. */
  history: GameState[];
  mountain: Mountain;
  players: Players;
  perspective: Perspective;
  selectedId: string | null;
  masonMode: MasonMode;
  cursor: Square;
  view: CameraView;
  /** Latest sentence for screen readers. */
  announcement: string;
  /** Team that offered a draw, waiting for the other side to answer. */
  drawOffer: Team | null;
  /** Fog, two humans: the board is hidden until the next player takes the screen. */
  curtain: boolean;
  /** Fog, two humans: hide the board between turns so players cannot see each other's view. */
  hideBetweenTurns: boolean;
  /** Fog only, for testing: show the whole board. */
  revealAll: boolean;
  /** A bot is choosing its move. */
  thinking: boolean;
  /** Bot games: bots wait while paused. */
  paused: boolean;
  /** Bot games: play exactly one bot move while paused. */
  stepPending: boolean;
  /** Bot games: pause before each bot move, in milliseconds. */
  botDelay: number;
  /** Seed for the bots' random choices in this game. */
  botSeed: number;
}

export interface LocalGameActions {
  start: (mountain: Mountain) => void;
  activate: (square: Square) => void;
  select: (pieceId: string | null) => void;
  setMasonMode: (mode: MasonMode) => void;
  setCursor: (square: Square) => void;
  setView: (view: CameraView) => void;
  undo: () => void;
  resign: () => void;
  offerDraw: () => void;
  answerDraw: (accept: boolean) => void;
  /** Fog, two humans: the next player has the screen. */
  lift: () => void;
  setHideBetweenTurns: (hide: boolean) => void;
  setRevealAll: (reveal: boolean) => void;
  setPerspective: (perspective: Perspective) => void;
  /**
   * Plays a bot's move and ends its thinking, unless the game moved on since the bot was asked
   * (`ply`).
   */
  playBot: (choice: PieceAction, ply: number) => void;
  setThinking: (thinking: boolean) => void;
  setPaused: (paused: boolean) => void;
  step: () => void;
  setBotDelay: (ms: number) => void;
}

export type LocalGameStore = LocalGameState & LocalGameActions;

export interface StoreOptions {
  players?: Players;
  botSeed?: number;
  botDelay?: number;
}

function freshGame(mountain: Mountain): GameState {
  const config = configFor(mountain);
  return mountain.kind === 'classic'
    ? newClassicGame(config)
    : newSeededGame(mountain.seed, config);
}

export function isBot(players: Players, team: Team): boolean {
  return players[team].kind === 'bot';
}

/** The only human player, when exactly one side is human. */
export function soloHuman(players: Players): Team | null {
  const humans = (['I', 'B'] as const).filter((t) => !isBot(players, t));
  return humans.length === 1 ? (humans[0] ?? null) : null;
}

function defaultPerspective(players: Players): Perspective {
  const human = soloHuman(players);
  if (human) return human;
  return isBot(players, 'I') ? 'all' : 'turn';
}

/** The team whose view is shown, or `null` for the whole board. */
export function viewerOf(s: Pick<LocalGameState, 'game' | 'perspective'>): Team | null {
  if (s.perspective === 'all') return null;
  return s.perspective === 'turn' ? s.game.turn : s.perspective;
}

/**
 * What the screen shows. Under fog, it is the view of the perspective's team (or everything once
 * the game is over, or when testing with "reveal all").
 */
export function shownGame(
  s: Pick<LocalGameState, 'game' | 'revealAll' | 'perspective'>,
): GameState {
  const { game } = s;
  const viewer = viewerOf(s);
  if (!game.config.fog || s.revealAll || game.winReason !== null || viewer === null) return game;
  return viewFor(game, viewer);
}

function viewOf(team: Team): CameraView {
  return team === 'I' ? 'ivory' : 'basalt';
}

function initial(mountain: Mountain, options: StoreOptions): LocalGameState {
  const players = options.players ?? TWO_HUMANS;
  const perspective = defaultPerspective(players);
  return {
    game: freshGame(mountain),
    history: [],
    mountain,
    players,
    perspective,
    selectedId: null,
    masonMode: 'move',
    cursor: perspective === 'B' ? { r: 0, c: 4 } : { r: 8, c: 4 },
    view: perspective === 'B' ? 'basalt' : 'ivory',
    announcement: '',
    drawOffer: null,
    curtain: false,
    hideBetweenTurns: true,
    revealAll: false,
    thinking: false,
    paused: false,
    stepPending: false,
    botDelay: options.botDelay ?? 400,
    botSeed: options.botSeed ?? 1,
  };
}

/** State of a game on one screen: two people, a person against a bot, or two bots. */
export function createLocalGameStore(mountain: Mountain, options: StoreOptions = {}) {
  return createStore<LocalGameStore>()((set, get) => {
    /** Records a new state after an action by either a person or a bot. */
    const advance = (next: GameState, sentence: string, extra: Partial<LocalGameState> = {}) => {
      const { game, perspective, hideBetweenTurns } = get();
      const outcome = describeOutcome(next);
      const followTurn = perspective === 'turn' && next.config.fog;
      set({
        game: next,
        history: [...get().history, game],
        selectedId: null,
        masonMode: 'move',
        drawOffer: null,
        curtain: followTurn && hideBetweenTurns && next.winReason === null,
        view: followTurn ? viewOf(next.turn) : get().view,
        announcement: outcome
          ? `${sentence} ${outcome}`
          : `${sentence} ${TEAM_LABEL[next.turn]} to move.`,
        ...extra,
      });
    };

    return {
      ...initial(mountain, options),

      start: (next) => {
        const s = get();
        set({
          ...initial(next, { players: s.players, botDelay: s.botDelay, botSeed: s.botSeed + 1 }),
          hideBetweenTurns: s.hideBetweenTurns,
          revealAll: s.revealAll,
          perspective: s.perspective,
          view: s.view,
          announcement: 'New game. Ivory to move.',
        });
      },

      lift: () => set({ curtain: false }),

      setHideBetweenTurns: (hideBetweenTurns) => set({ hideBetweenTurns }),

      setRevealAll: (revealAll) => set({ revealAll, selectedId: null }),

      setPerspective: (perspective) => set({ perspective, selectedId: null }),

      select: (pieceId) => set({ selectedId: pieceId, masonMode: 'move' }),

      setMasonMode: (masonMode) => set({ masonMode }),

      setCursor: (cursor) => set({ cursor }),

      setView: (view) => set({ view }),

      activate: (square) => {
        const { game, selectedId, masonMode, curtain, players } = get();
        if (curtain || isBot(players, game.turn)) return;
        // Under fog the player picks from their own view; the engine settles it on the real board.
        const shown = shownGame(get());
        const selected = shown.pieces.find((p) => p.id === selectedId && p.alive);
        const action = actionAt(availableActions(shown, selected, masonMode), square);

        if (selected && action) {
          const next = applyAction(game, selected.id, action);
          advance(next, describeAction(shown, selected.id, action) + fogNote(next));
          return;
        }

        const clicked = shown.pieces.find((p) => p.alive && p.r === square.r && p.c === square.c);
        if (!clicked || clicked.id === selectedId) {
          set({ selectedId: null, masonMode: 'move' });
          return;
        }
        const note =
          clicked.team !== game.turn
            ? `${TEAM_LABEL[clicked.team]} piece. It is ${TEAM_LABEL[game.turn]}'s turn.`
            : isPinned(game, clicked)
              ? 'This piece is pinned and cannot act this turn.'
              : '';
        set({
          selectedId: clicked.id,
          masonMode: 'move',
          announcement: note || get().announcement,
        });
      },

      playBot: (choice, ply) => {
        const { game } = get();
        if (game.ply !== ply || game.winReason !== null) {
          set({ thinking: false });
          return;
        }
        const next = applyAction(game, choice.pieceId, choice.action);
        const entry = next.log[next.log.length - 1];
        const viewer = viewerOf({ game: next, perspective: get().perspective });
        const hidden = next.config.fog && viewer !== null && entry?.hiddenFrom === viewer;
        advance(
          next,
          hidden
            ? `${TEAM_LABEL[game.turn]} moved out of sight.`
            : describeAction(game, choice.pieceId, choice.action) + fogNote(next),
          // One update: the move and the end of thinking, so the driver never sees them apart.
          { thinking: false, stepPending: false },
        );
      },

      setThinking: (thinking) => set({ thinking }),

      setPaused: (paused) => set({ paused, stepPending: false }),

      step: () => set({ paused: true, stepPending: true }),

      setBotDelay: (botDelay) => set({ botDelay }),

      undo: () => {
        const { history, players } = get();
        if (isBot(players, 'I') && isBot(players, 'B')) return;
        // Against a bot, go back to the last position where a person was to move.
        let index = history.length - 1;
        while (index >= 0 && isBot(players, history[index]?.turn ?? 'I')) index--;
        const previous = history[index];
        if (!previous) return;
        const followTurn = get().perspective === 'turn' && previous.config.fog;
        set({
          game: previous,
          history: history.slice(0, index),
          selectedId: null,
          masonMode: 'move',
          drawOffer: null,
          curtain: false,
          thinking: false,
          view: followTurn ? viewOf(previous.turn) : get().view,
          announcement: `Move undone. ${TEAM_LABEL[previous.turn]} to move.`,
        });
      },

      resign: () => {
        const { game, players } = get();
        if (game.winReason !== null) return;
        const next = resign(game, soloHuman(players) ?? game.turn);
        set({
          game: next,
          history: [...get().history, game],
          selectedId: null,
          announcement: describeOutcome(next),
        });
      },

      offerDraw: () => {
        const { game } = get();
        if (game.winReason !== null) return;
        const other = TEAM_LABEL[otherTeam(game.turn)];
        set({
          drawOffer: game.turn,
          announcement: `${TEAM_LABEL[game.turn]} offers a draw. ${other}, accept or decline.`,
        });
      },

      answerDraw: (accept) => {
        const { game, drawOffer } = get();
        if (drawOffer === null) return;
        if (!accept) {
          set({ drawOffer: null, announcement: 'Draw declined.' });
          return;
        }
        const next = agreeDraw(game);
        set({
          game: next,
          history: [...get().history, game],
          drawOffer: null,
          selectedId: null,
          announcement: describeOutcome(next),
        });
      },
    };
  });
}

/** Under fog, what really happened when it differs from what the player chose. */
function fogNote(next: GameState): string {
  const entry = next.log[next.log.length - 1];
  if (!entry || !next.config.fog) return '';
  if (entry.blocked) return ' Blocked: the way was not as it looked.';
  if (entry.result?.kind === 'capture') return ' It ran into a hidden piece and captured it.';
  if (entry.result) return ' It was stopped short.';
  return '';
}

export type LocalGameStoreApi = ReturnType<typeof createLocalGameStore>;
